import type { Rail, Vec2 } from "../level";
import { checkColor, rad, withColor, type PieceKind, type Placed, type PrimKind } from "./types";

/**
 * A hubba (#152): a granite ledge running down beside a stair set, its top
 * sloping from `h` at the -u end to `hb` at the +u end (the way a stair set
 * goes down, so a hubba beside one takes the same `rot`), ±hu along and ±hw
 * across. Both long edges grind, as sloped ledges (`Rail.yb`).
 */
export interface HubbaPrim extends Placed {
  kind: "hubba";
  hu: number;
  h: number;
  hb: number;
  /** Render only: its own color. */
  color?: string;
}

export interface HubbaPiece {
  kind: "hubba";
  x: number;
  z: number;
  rot?: number;
  hu: number;
  hw: number;
  /** Height at the top (-u) end. */
  h: number;
  /** Height at the bottom (+u) end. */
  hb: number;
  color?: string;
}

/** Its top's height at u. */
export const hubbaTop = (p: Pick<HubbaPrim, "hu" | "h" | "hb">, u: number): number => p.h + ((p.hb - p.h) * (u + p.hu)) / (2 * p.hu);

export const HUBBA: PrimKind<HubbaPrim> = {
  eval: (p, u, w) => (Math.abs(u) <= p.hu && Math.abs(w) <= p.hw ? { h: hubbaTop(p, u), du: (p.hb - p.h) / (2 * p.hu), dw: 0 } : null),
  extent: (p) => [-p.hu, p.hu],
  shadow: 0.55,
  top: (p) => Math.max(p.h, p.hb),
  plan: (p) => [{ u0: -p.hu, u1: p.hu, fill: "box" }],
};

/** The hubba's two long edges, each a ledge sloping from `h` down to `hb`. */
export function hubbaLedges(p: HubbaPrim): Rail[] {
  const c = Math.cos(p.rot);
  const s = Math.sin(p.rot);
  const toWorld = (u: number, w: number): Vec2 => ({ x: p.x + u * c - w * s, z: p.z + u * s + w * c });
  return [p.hw, -p.hw].map((w) => ({ a: toWorld(-p.hu, w), b: toWorld(p.hu, w), y: p.h, yb: p.hb, kind: "ledge" as const }));
}

export const HUBBA_PIECE: PieceKind<HubbaPiece> = {
  fields: ["x", "z", "rot", "hu", "hw", "h", "hb", "color"],
  placed: true,
  check(c, o, path, bounds) {
    c.num(o, "hu", path, { min: 0.5, max: 2 * bounds });
    c.num(o, "h", path, { min: 0.1, max: 6 });
    c.num(o, "hb", path, { min: 0.1, max: 6 });
    checkColor(c, o, path);
  },
  compile(p, out) {
    const prim = withColor<HubbaPrim>({ kind: "hubba", x: p.x, z: p.z, rot: rad(p.rot), hu: p.hu, hw: p.hw, h: p.h, hb: p.hb }, p.color);
    out.prims.push(prim);
    out.rails.push(...hubbaLedges(prim));
  },
};
